import { useGetRegimeCurrentQuery } from "@/store/api";
import { Badge } from "@/components/ui/badge";

/** Compact market-regime pill — shows the active risk_on / neutral /
 *  risk_off state with the top signal reason. Tooltip lists every
 *  signal. Hidden when the overlay is disabled in settings (no
 *  behavioral effect to surface). Distinct from `RegimePill` which
 *  is a per-symbol regime badge (uptrend / range / chaos / ...). */
export function MarketRegimePill() {
  const { data } = useGetRegimeCurrentQuery(undefined, {
    pollingInterval: 60_000,
  });
  if (!data) return null;
  if (!data.overlay_enabled) return null;

  const state = (data.state || "neutral") as
    "risk_on" | "neutral" | "risk_off";
  const forced = data.forced_state && data.forced_state !== "auto";

  const tone: "good" | "warn" | "bad" | "muted" =
    state === "risk_on" ? "good"
    : state === "risk_off" ? "bad"
    : "muted";

  // Clear, explicit labels — "RISK ON/OFF/NEUTRAL" was confusing.
  // The label tells you what the bot DOES, not just abstract state.
  const label =
    state === "risk_on"  ? "Aggressive · trail wide, DCA on"
    : state === "risk_off" ? "Defensive · trail tight, DCA off"
    : "Watching · default behaviour";

  const shortLabel =
    state === "risk_on" ? "AGGRESSIVE"
    : state === "risk_off" ? "DEFENSIVE"
    : "WATCHING";

  const reasons: Array<{ signal: string; verdict?: string }> = (data.reasons || []) as any;
  const tooltip = reasons
    .map((r) => `${r.signal}: ${r.verdict ?? "—"}`)
    .join("\n");
  // Build a single best-signal summary instead of just dumping the name
  const topReasons = reasons
    .filter((r) => r.verdict && r.verdict !== "flat" && r.verdict !== "clear")
    .slice(0, 2);
  const topSummary =
    topReasons.length > 0
      ? topReasons.map((r) => `${r.signal.replace(/_/g, " ")} → ${r.verdict}`).join(", ")
      : "all signals flat / clear";

  const scoreNum: number | undefined =
    typeof (data as any).score === "number" ? (data as any).score : undefined;

  return (
    <div
      className="flex flex-col gap-1 px-3 py-2 rounded-md border border-border bg-bg2 text-xs"
      title={tooltip}
    >
      <div className="flex items-baseline gap-3 flex-wrap">
        <span className="text-muted uppercase tracking-wide">Bot mode</span>
        <span className="tabular text-fg font-semibold">{shortLabel}</span>
        <Badge tone={tone}>{label.split(" · ")[0]}</Badge>
        {forced && (
          <Badge tone="warn" className="ml-1">
            operator override
          </Badge>
        )}
        {scoreNum !== undefined && (
          <span className="text-muted tabular">score {scoreNum > 0 ? "+" : ""}{scoreNum}</span>
        )}
      </div>
      <div className="text-muted">
        {label.split(" · ")[1] && (
          <span className="text-fg/70">{label.split(" · ")[1]}</span>
        )}
        <span className="mx-2 opacity-50">·</span>
        <span>{forced ? `forced ${state} by operator` : topSummary}</span>
      </div>
    </div>
  );
}
